/**
 * Packaging OS — Public Services & Pricing page (MM7).
 *
 * Honest commercial surface: service packs + plan cards from the
 * PUBLIC api projection. Every card carries the foundation/draft/
 * human-review badges; there is no checkout and no fake pricing.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getPublicPlans,
  getPublicServicePacks,
  type PublicServicePack,
} from "../api/client";
import { Button } from "../components/Button";
import { Footer } from "../components/Footer";
import { PublicHeader } from "../components/PublicHeader";
import { TrustBanner } from "../components/TrustBanner";
import { strings } from "../i18n/strings";
import { useLocale } from "../i18n/useLocale";
import { useLocaleSwitch } from "../i18n/useLocaleSwitch";

type PacksState =
  | { kind: "loading" }
  | { kind: "loaded"; packs: PublicServicePack[]; plans: Record<string, unknown>[] };

export function ServicesPage(): JSX.Element {
  const [locale] = useLocale();
  const setLocale = useLocaleSwitch();
  const t = strings(locale);
  const navigate = useNavigate();
  const [state, setState] = useState<PacksState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [packsResult, plansResult] = await Promise.all([
        getPublicServicePacks(),
        getPublicPlans(),
      ]);
      if (cancelled) return;
      const packs =
        packsResult.outcome === "ok" ? packsResult.data?.items ?? [] : [];
      const plans =
        plansResult.outcome === "ok"
          ? ((plansResult.data as { items?: Record<string, unknown>[] })?.items ?? [])
          : [];
      setState({ kind: "loaded", packs, plans });
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="po-page">
      <PublicHeader
        locale={locale}
        onLocaleChange={setLocale}
        switchLanguageLabel={t.switchLanguageLabel}
        brand={t.appName}
        navLinks={[
          { to: "/request", label: t.ctaRequestAccess },
          { to: "/login", label: t.ctaSignInWithGoogle.split(" ")[0] },
        ]}
      />

      <main className="po-page-main">
        <TrustBanner message={t.trustBannerFull} />

        <section aria-labelledby="po-services-title" className="po-hero">
          <h1 id="po-services-title" className="po-hero__title">
            {t.servicesTitle}
          </h1>
          <p className="po-hero__subtitle">{t.servicesIntro}</p>
          <ul className="po-trust-strip__list">
            <li>{t.servicesHumanReview}</li>
            <li>{t.servicesNotLegal}</li>
            <li>{t.servicesNoBilling}</li>
          </ul>
        </section>

        <section aria-label={t.servicesTitle}>
          {state.kind === "loading" ? (
            <p role="status">…</p>
          ) : (
            <div className="po-capability-grid" data-testid="pack-grid">
              {state.packs.map((pack) => (
                <article
                  key={pack.pack_id}
                  className="po-capability-card"
                  data-testid={`pack-${pack.pack_id}`}
                >
                  <h3>{pack.name}</h3>
                  <p>{pack.positioning}</p>
                  <p>
                    <strong>{t.packWorkflows}:</strong>{" "}
                    {pack.included_workflows.join("، ").length > 0
                      ? pack.included_workflows.join(", ")
                      : "—"}
                  </p>
                  <p>
                    <span className="po-status-pill">{t.packFoundation}</span>{" "}
                    {pack.pilot_ready ? (
                      <span className="po-status-pill">{t.packPilotReady}</span>
                    ) : null}{" "}
                    <span className="po-status-pill">{t.pricingDraftLabel}</span>
                  </p>
                  {pack.known_gaps.length > 0 ? (
                    <p>
                      <strong>{t.packKnownGaps}:</strong>{" "}
                      {pack.known_gaps.join("; ")}
                    </p>
                  ) : null}
                  <p className="po-disclaimer">{pack.disclaimer}</p>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      navigate(`/request?pack=${encodeURIComponent(pack.pack_id)}`)
                    }
                  >
                    {t.packRequestQuote}
                  </Button>
                </article>
              ))}
            </div>
          )}
        </section>

        <section aria-labelledby="po-pricing-title">
          <h2 id="po-pricing-title">{t.pricingTitle}</h2>
          <p>{t.pricingIntro}</p>
          {state.kind === "loaded" ? (
            <div className="po-capability-grid" data-testid="plan-grid">
              {state.plans.map((plan) => (
                <article key={String(plan.plan_id)} className="po-capability-card">
                  <h3>{String(plan.name)}</h3>
                  <p>{String(plan.positioning)}</p>
                  <p>
                    <span className="po-status-pill">{t.pricingDraftLabel}</span>
                  </p>
                  <Button variant="secondary" onClick={() => navigate("/request")}>
                    {t.packRequestQuote}
                  </Button>
                </article>
              ))}
            </div>
          ) : null}
        </section>
      </main>

      <Footer copyright={t.copyrightLine} />
    </div>
  );
}
